/**
 * Pre-designed Menu Templates
 * 
 * Template-based theming system for idemenu.
 * - STARTER plan: 3 FREE templates (no customization)
 * - PRO plan: 10 templates + primary/secondary color customization
 * - ENTERPRISE: Future custom designs
 */

// ============================================
// FREE TEMPLATES (STARTER Plan)
// ============================================

export const FREE_TEMPLATES = {
  'clean-list': {
    id: 'clean-list',
    name: 'Clean List',
    nameKey: 'templates.cleanList.name',
    description: 'Super clean and fast design for any restaurant',
    descriptionKey: 'templates.cleanList.description',
    bestFor: ['any', 'fast-food', 'casual'],
    bestForKey: 'templates.cleanList.bestFor',
    tier: 'FREE',
    mode: 'light',
    preview: '/templates/clean-list-preview.png',
    
    // Layout settings
    layout: {
      mode: 'no-image', // List-only, no images
      itemStyle: 'simple-row', // Minimal row design
      categoryStyle: 'header-only', // Simple header, no background
      spacing: 'compact',
      showBranding: true, // "Powered by idemenu" footer
    },
    
    // Color palette
    colors: {
      primary: '#1a1a1a',
      secondary: '#4a4a4a',
      accent: '#2563eb',
      background: '#ffffff',
      surface: '#ffffff',
      text: '#1a1a1a',
      textMuted: '#6b7280',
      border: '#e5e7eb',
      headerBg: '#1a1a1a',
      headerText: '#ffffff',
      categoryBg: 'transparent',
      categoryText: '#1a1a1a',
      cardBg: '#ffffff',
      priceBadge: '#2563eb',
    },
    
    // Typography
    typography: {
      headingFont: 'Inter',
      bodyFont: 'Inter',
      headingWeight: 700,
      bodyWeight: 400,
      restaurantNameSize: '20px',
      categoryTitleSize: '18px',
      itemNameSize: '15px',
      itemDescSize: '13px',
      priceSize: '15px',
    },
    
    // Spacing
    spacing: {
      cardRadius: '0px',
      buttonRadius: '6px',
      itemPadding: '12px 16px',
      categoryGap: '24px',
      itemGap: '0px',
    },
  },

  'warm-bistro': {
    id: 'warm-bistro',
    name: 'Warm Bistro',
    nameKey: 'templates.warmBistro.name',
    description: 'Cozy and inviting for home-cooking, lokanta, breakfast places',
    descriptionKey: 'templates.warmBistro.description',
    bestFor: ['lokanta', 'breakfast', 'home-cooking'],
    bestForKey: 'templates.warmBistro.bestFor',
    tier: 'FREE',
    mode: 'light',
    preview: '/templates/warm-bistro-preview.png',
    
    layout: {
      mode: 'left', // Small thumbnails on left
      itemStyle: 'subtle-card',
      categoryStyle: 'block',
      spacing: 'comfortable',
      showBranding: true,
    },
    
    colors: {
      primary: '#b45309',
      secondary: '#d97706',
      accent: '#f59e0b',
      background: '#fffbeb',
      surface: '#ffffff',
      text: '#78350f',
      textMuted: '#92400e',
      border: '#fde68a',
      headerBg: '#b45309',
      headerText: '#ffffff',
      categoryBg: '#fef3c7',
      categoryText: '#78350f',
      cardBg: '#ffffff',
      priceBadge: '#d97706',
    },
    
    typography: {
      headingFont: 'Georgia',
      bodyFont: 'Inter',
      headingWeight: 700,
      bodyWeight: 400,
      restaurantNameSize: '22px',
      categoryTitleSize: '20px',
      itemNameSize: '16px',
      itemDescSize: '13px',
      priceSize: '16px',
    },
    
    spacing: {
      cardRadius: '8px',
      buttonRadius: '8px',
      itemPadding: '14px 16px',
      categoryGap: '28px',
      itemGap: '10px',
    },
  },

  'night-chalk': {
    id: 'night-chalk',
    name: 'Night Chalk',
    nameKey: 'templates.nightChalk.name',
    description: 'Bold dark mode for bars, late-night, street food',
    descriptionKey: 'templates.nightChalk.description',
    bestFor: ['bar', 'late-night', 'street-food'],
    bestForKey: 'templates.nightChalk.bestFor',
    tier: 'FREE',
    mode: 'dark',
    preview: '/templates/night-chalk-preview.png',
    
    layout: {
      mode: 'no-image',
      itemStyle: 'compact-row',
      categoryStyle: 'chalk-header',
      spacing: 'tight',
      showBranding: true,
    },
    
    colors: {
      primary: '#fbbf24',
      secondary: '#fcd34d',
      accent: '#fbbf24',
      background: '#1a1a1a',
      surface: '#262626',
      text: '#f5f5f5',
      textMuted: '#a3a3a3',
      border: '#404040',
      headerBg: '#0a0a0a',
      headerText: '#fbbf24',
      categoryBg: '#262626',
      categoryText: '#fbbf24',
      cardBg: '#1a1a1a',
      priceBadge: '#fbbf24',
    },
    
    typography: {
      headingFont: 'Manrope',
      bodyFont: 'Inter',
      headingWeight: 800,
      bodyWeight: 400,
      restaurantNameSize: '20px',
      categoryTitleSize: '18px',
      itemNameSize: '15px',
      itemDescSize: '12px',
      priceSize: '18px',
    },
    
    spacing: {
      cardRadius: '0px',
      buttonRadius: '4px',
      itemPadding: '10px 14px',
      categoryGap: '20px',
      itemGap: '2px',
    },
  },
};

// ============================================
// PRO TEMPLATES (PRO Plan)
// ============================================

export const PRO_TEMPLATES = {
  'noir-fine-dining': {
    id: 'noir-fine-dining',
    name: 'Noir Fine Dining',
    nameKey: 'templates.noirFineDining.name',
    description: 'Elegant and premium for fine dining, chef\'s table, wine bar',
    descriptionKey: 'templates.noirFineDining.description',
    bestFor: ['fine-dining', 'wine-bar', 'chefs-table'],
    bestForKey: 'templates.noirFineDining.bestFor',
    tier: 'PRO',
    mode: 'dark',
    preview: '/templates/noir-fine-dining-preview.png',
    
    layout: {
      mode: 'no-image',
      itemStyle: 'elegant-row',
      categoryStyle: 'minimal-divider',
      spacing: 'spacious',
      showBranding: false,
    },
    
    colors: {
      primary: '#d4af37', // Gold
      secondary: '#b8962e',
      accent: '#d4af37',
      background: '#0f0f0f',
      surface: '#1a1a1a',
      text: '#f5f5f5',
      textMuted: '#9ca3af',
      border: '#2a2a2a',
      headerBg: '#0a0a0a',
      headerText: '#d4af37',
      categoryBg: 'transparent',
      categoryText: '#d4af37',
      cardBg: 'transparent',
      priceBadge: '#d4af37',
    },
    
    typography: {
      headingFont: 'Playfair Display',
      bodyFont: 'Inter',
      headingWeight: 500,
      bodyWeight: 300,
      restaurantNameSize: '24px',
      categoryTitleSize: '20px',
      itemNameSize: '17px',
      itemDescSize: '14px',
      priceSize: '16px',
    },
    
    spacing: {
      cardRadius: '0px',
      buttonRadius: '2px',
      itemPadding: '20px 24px',
      categoryGap: '40px',
      itemGap: '16px',
    },
  },

  'coastal-breeze': {
    id: 'coastal-breeze',
    name: 'Coastal Breeze',
    nameKey: 'templates.coastalBreeze.name',
    description: 'Airy and fresh for seafood, meyhane, coastal restaurants',
    descriptionKey: 'templates.coastalBreeze.description',
    bestFor: ['seafood', 'meyhane', 'coastal'],
    bestForKey: 'templates.coastalBreeze.bestFor',
    tier: 'PRO',
    mode: 'light',
    preview: '/templates/coastal-breeze-preview.png',
    
    layout: {
      mode: 'left',
      itemStyle: 'photo-card',
      categoryStyle: 'wave-header',
      spacing: 'airy',
      showBranding: false,
    },
    
    colors: {
      primary: '#0369a1',
      secondary: '#0284c7',
      accent: '#06b6d4',
      background: '#f0f9ff',
      surface: '#ffffff',
      text: '#0c4a6e',
      textMuted: '#0369a1',
      border: '#bae6fd',
      headerBg: '#0369a1',
      headerText: '#ffffff',
      categoryBg: '#e0f2fe',
      categoryText: '#0c4a6e',
      cardBg: '#ffffff',
      priceBadge: '#0284c7',
    },
    
    typography: {
      headingFont: 'Manrope',
      bodyFont: 'Inter',
      headingWeight: 600,
      bodyWeight: 400,
      restaurantNameSize: '22px',
      categoryTitleSize: '20px',
      itemNameSize: '16px',
      itemDescSize: '14px',
      priceSize: '16px',
    },
    
    spacing: {
      cardRadius: '16px',
      buttonRadius: '12px',
      itemPadding: '16px',
      categoryGap: '32px',
      itemGap: '16px',
    },
  },

  'anatolian-grill': {
    id: 'anatolian-grill',
    name: 'Anatolian Grill',
    nameKey: 'templates.anatolianGrill.name',
    description: 'Strong and hearty for kebab, ocakbaşı, Turkish cuisine',
    descriptionKey: 'templates.anatolianGrill.description',
    bestFor: ['kebab', 'ocakbasi', 'turkish'],
    bestForKey: 'templates.anatolianGrill.bestFor',
    tier: 'PRO',
    mode: 'dark',
    preview: '/templates/anatolian-grill-preview.png',
    
    layout: {
      mode: 'left',
      itemStyle: 'bold-card',
      categoryStyle: 'strong-divider',
      spacing: 'comfortable',
      showBranding: false,
    },
    
    colors: {
      primary: '#dc2626',
      secondary: '#b91c1c',
      accent: '#f59e0b',
      background: '#1c1917',
      surface: '#292524',
      text: '#fafaf9',
      textMuted: '#a8a29e',
      border: '#44403c',
      headerBg: '#7c2d12',
      headerText: '#fef3c7',
      categoryBg: '#292524',
      categoryText: '#f59e0b',
      cardBg: '#292524',
      priceBadge: '#dc2626',
    },
    
    typography: {
      headingFont: 'Manrope',
      bodyFont: 'Inter',
      headingWeight: 800,
      bodyWeight: 400,
      restaurantNameSize: '22px',
      categoryTitleSize: '20px',
      itemNameSize: '16px',
      itemDescSize: '13px',
      priceSize: '17px',
    },
    
    spacing: {
      cardRadius: '8px',
      buttonRadius: '6px',
      itemPadding: '14px 16px',
      categoryGap: '28px',
      itemGap: '12px',
    },
  },

  'cafe-latte': {
    id: 'cafe-latte',
    name: 'Cafe Latte',
    nameKey: 'templates.cafeLatte.name',
    description: 'Friendly and warm for cafe, bakery, brunch',
    descriptionKey: 'templates.cafeLatte.description',
    bestFor: ['cafe', 'bakery', 'brunch'],
    bestForKey: 'templates.cafeLatte.bestFor',
    tier: 'PRO',
    mode: 'light',
    preview: '/templates/cafe-latte-preview.png',
    
    layout: {
      mode: 'top',
      itemStyle: 'image-card',
      categoryStyle: 'cozy-header',
      spacing: 'comfortable',
      showBranding: false,
    },
    
    colors: {
      primary: '#78350f',
      secondary: '#92400e',
      accent: '#d97706',
      background: '#fefce8',
      surface: '#ffffff',
      text: '#422006',
      textMuted: '#78350f',
      border: '#fde68a',
      headerBg: '#78350f',
      headerText: '#fef3c7',
      categoryBg: '#fef9c3',
      categoryText: '#78350f',
      cardBg: '#ffffff',
      priceBadge: '#92400e',
    },
    
    typography: {
      headingFont: 'Manrope',
      bodyFont: 'Inter',
      headingWeight: 600,
      bodyWeight: 400,
      restaurantNameSize: '22px',
      categoryTitleSize: '18px',
      itemNameSize: '15px',
      itemDescSize: '13px',
      priceSize: '15px',
    },
    
    spacing: {
      cardRadius: '16px',
      buttonRadius: '20px',
      itemPadding: '0',
      categoryGap: '28px',
      itemGap: '16px',
    },
  },

  'street-neon': {
    id: 'street-neon',
    name: 'Street Neon',
    nameKey: 'templates.streetNeon.name',
    description: 'Bold and fast for fast food, street food, döner/pizza',
    descriptionKey: 'templates.streetNeon.description',
    bestFor: ['fast-food', 'street-food', 'doner', 'pizza'],
    bestForKey: 'templates.streetNeon.bestFor',
    tier: 'PRO',
    mode: 'dark',
    preview: '/templates/street-neon-preview.png',
    
    layout: {
      mode: 'left',
      itemStyle: 'neon-card',
      categoryStyle: 'neon-header',
      spacing: 'compact',
      showBranding: false,
    },
    
    colors: {
      primary: '#22c55e',
      secondary: '#10b981',
      accent: '#f43f5e',
      background: '#09090b',
      surface: '#18181b',
      text: '#fafafa',
      textMuted: '#a1a1aa',
      border: '#3f3f46',
      headerBg: '#09090b',
      headerText: '#22c55e',
      categoryBg: '#18181b',
      categoryText: '#22c55e',
      cardBg: '#18181b',
      priceBadge: '#f43f5e',
    },
    
    typography: {
      headingFont: 'Manrope',
      bodyFont: 'Inter',
      headingWeight: 900,
      bodyWeight: 500,
      restaurantNameSize: '22px',
      categoryTitleSize: '18px',
      itemNameSize: '15px',
      itemDescSize: '12px',
      priceSize: '18px',
    },
    
    spacing: {
      cardRadius: '8px',
      buttonRadius: '6px',
      itemPadding: '12px 14px',
      categoryGap: '24px',
      itemGap: '8px',
    },
  },

  'green-clean': {
    id: 'green-clean',
    name: 'Green & Clean',
    nameKey: 'templates.greenClean.name',
    description: 'Fresh and healthy for vegan, salad bar, fit food',
    descriptionKey: 'templates.greenClean.description',
    bestFor: ['vegan', 'healthy', 'salad', 'fit-food'],
    bestForKey: 'templates.greenClean.bestFor',
    tier: 'PRO',
    mode: 'light',
    preview: '/templates/green-clean-preview.png',
    
    layout: {
      mode: 'top',
      itemStyle: 'grid-card',
      categoryStyle: 'leaf-header',
      spacing: 'comfortable',
      showBranding: false,
    },
    
    colors: {
      primary: '#16a34a',
      secondary: '#22c55e',
      accent: '#84cc16',
      background: '#f0fdf4',
      surface: '#ffffff',
      text: '#14532d',
      textMuted: '#166534',
      border: '#bbf7d0',
      headerBg: '#16a34a',
      headerText: '#ffffff',
      categoryBg: '#dcfce7',
      categoryText: '#14532d',
      cardBg: '#ffffff',
      priceBadge: '#16a34a',
    },
    
    typography: {
      headingFont: 'Inter',
      bodyFont: 'Inter',
      headingWeight: 600,
      bodyWeight: 400,
      restaurantNameSize: '22px',
      categoryTitleSize: '18px',
      itemNameSize: '15px',
      itemDescSize: '13px',
      priceSize: '15px',
    },
    
    spacing: {
      cardRadius: '12px',
      buttonRadius: '8px',
      itemPadding: '0',
      categoryGap: '28px',
      itemGap: '16px',
    },
  },

  'sweet-studio': {
    id: 'sweet-studio',
    name: 'Sweet Studio',
    nameKey: 'templates.sweetStudio.name',
    description: 'Playful and colorful for dessert, ice cream, patisserie',
    descriptionKey: 'templates.sweetStudio.description',
    bestFor: ['dessert', 'ice-cream', 'patisserie'],
    bestForKey: 'templates.sweetStudio.bestFor',
    tier: 'PRO',
    mode: 'light',
    preview: '/templates/sweet-studio-preview.png',
    
    layout: {
      mode: 'top',
      itemStyle: 'playful-card',
      categoryStyle: 'candy-header',
      spacing: 'comfortable',
      showBranding: false,
    },
    
    colors: {
      primary: '#ec4899',
      secondary: '#f472b6',
      accent: '#a855f7',
      background: '#fdf2f8',
      surface: '#ffffff',
      text: '#831843',
      textMuted: '#be185d',
      border: '#fbcfe8',
      headerBg: '#ec4899',
      headerText: '#ffffff',
      categoryBg: '#fce7f3',
      categoryText: '#831843',
      cardBg: '#ffffff',
      priceBadge: '#ec4899',
    },
    
    typography: {
      headingFont: 'Manrope',
      bodyFont: 'Inter',
      headingWeight: 700,
      bodyWeight: 400,
      restaurantNameSize: '24px',
      categoryTitleSize: '20px',
      itemNameSize: '16px',
      itemDescSize: '13px',
      priceSize: '16px',
    },
    
    spacing: {
      cardRadius: '20px',
      buttonRadius: '20px',
      itemPadding: '0',
      categoryGap: '32px',
      itemGap: '16px',
    },
  },

  'taproom': {
    id: 'taproom',
    name: 'Taproom',
    nameKey: 'templates.taproom.name',
    description: 'Dark and glossy for pub, bar, craft beer, cocktails',
    descriptionKey: 'templates.taproom.description',
    bestFor: ['pub', 'bar', 'craft-beer', 'cocktails'],
    bestForKey: 'templates.taproom.bestFor',
    tier: 'PRO',
    mode: 'dark',
    preview: '/templates/taproom-preview.png',
    
    layout: {
      mode: 'left',
      itemStyle: 'drink-card',
      categoryStyle: 'bar-header',
      spacing: 'comfortable',
      showBranding: false,
    },
    
    colors: {
      primary: '#fbbf24',
      secondary: '#f59e0b',
      accent: '#06b6d4',
      background: '#0c0a09',
      surface: '#1c1917',
      text: '#fafaf9',
      textMuted: '#a8a29e',
      border: '#292524',
      headerBg: '#0c0a09',
      headerText: '#fbbf24',
      categoryBg: '#1c1917',
      categoryText: '#fbbf24',
      cardBg: '#1c1917',
      priceBadge: '#fbbf24',
    },
    
    typography: {
      headingFont: 'Manrope',
      bodyFont: 'Inter',
      headingWeight: 700,
      bodyWeight: 400,
      restaurantNameSize: '22px',
      categoryTitleSize: '18px',
      itemNameSize: '16px',
      itemDescSize: '13px',
      priceSize: '16px',
    },
    
    spacing: {
      cardRadius: '8px',
      buttonRadius: '6px',
      itemPadding: '14px 16px',
      categoryGap: '28px',
      itemGap: '10px',
    },
  },

  'pizza-press': {
    id: 'pizza-press',
    name: 'Pizza Press',
    nameKey: 'templates.pizzaPress.name',
    description: 'Bold product focus for pizza, burger, casual dining',
    descriptionKey: 'templates.pizzaPress.description',
    bestFor: ['pizza', 'burger', 'casual-dining'],
    bestForKey: 'templates.pizzaPress.bestFor',
    tier: 'PRO',
    mode: 'light',
    preview: '/templates/pizza-press-preview.png',
    
    layout: {
      mode: 'top',
      itemStyle: 'product-card',
      categoryStyle: 'bold-header',
      spacing: 'comfortable',
      showBranding: false,
    },
    
    colors: {
      primary: '#dc2626',
      secondary: '#ea580c',
      accent: '#fbbf24',
      background: '#fef2f2',
      surface: '#ffffff',
      text: '#1c1917',
      textMuted: '#57534e',
      border: '#fecaca',
      headerBg: '#dc2626',
      headerText: '#ffffff',
      categoryBg: '#fee2e2',
      categoryText: '#991b1b',
      cardBg: '#ffffff',
      priceBadge: '#dc2626',
    },
    
    typography: {
      headingFont: 'Manrope',
      bodyFont: 'Inter',
      headingWeight: 800,
      bodyWeight: 400,
      restaurantNameSize: '24px',
      categoryTitleSize: '20px',
      itemNameSize: '17px',
      itemDescSize: '13px',
      priceSize: '17px',
    },
    
    spacing: {
      cardRadius: '12px',
      buttonRadius: '8px',
      itemPadding: '0',
      categoryGap: '32px',
      itemGap: '16px',
    },
  },

  'hotel-suite': {
    id: 'hotel-suite',
    name: 'Hotel Suite',
    nameKey: 'templates.hotelSuite.name',
    description: 'Structured and readable for hotel room service, multi-language',
    descriptionKey: 'templates.hotelSuite.description',
    bestFor: ['hotel', 'room-service', 'corporate'],
    bestForKey: 'templates.hotelSuite.bestFor',
    tier: 'PRO',
    mode: 'light',
    preview: '/templates/hotel-suite-preview.png',
    
    layout: {
      mode: 'left',
      itemStyle: 'structured-row',
      categoryStyle: 'formal-header',
      spacing: 'spacious',
      showBranding: false,
    },
    
    colors: {
      primary: '#1e3a5f',
      secondary: '#2d5a8c',
      accent: '#c9a961',
      background: '#f8fafc',
      surface: '#ffffff',
      text: '#1e293b',
      textMuted: '#64748b',
      border: '#e2e8f0',
      headerBg: '#1e3a5f',
      headerText: '#ffffff',
      categoryBg: '#f1f5f9',
      categoryText: '#1e3a5f',
      cardBg: '#ffffff',
      priceBadge: '#1e3a5f',
    },
    
    typography: {
      headingFont: 'Inter',
      bodyFont: 'Inter',
      headingWeight: 600,
      bodyWeight: 400,
      restaurantNameSize: '22px',
      categoryTitleSize: '18px',
      itemNameSize: '15px',
      itemDescSize: '14px',
      priceSize: '15px',
    },
    
    spacing: {
      cardRadius: '4px',
      buttonRadius: '4px',
      itemPadding: '16px 20px',
      categoryGap: '36px',
      itemGap: '12px',
    },
  },
};

// ============================================
// COMBINED EXPORTS
// ============================================

export const ALL_TEMPLATES = {
  ...FREE_TEMPLATES,
  ...PRO_TEMPLATES,
};

/**
 * Get templates available for a specific plan
 */
export const getTemplatesForPlan = (plan) => {
  const templates = [];
  
  // STARTER and all plans get FREE templates
  Object.values(FREE_TEMPLATES).forEach(t => templates.push(t));
  
  // PRO and ENTERPRISE get PRO templates
  if (plan === 'PRO' || plan === 'ENTERPRISE') {
    Object.values(PRO_TEMPLATES).forEach(t => templates.push(t));
  }
  
  return templates;
};

/**
 * Get a template by ID
 */
export const getTemplateById = (templateId) => {
  return ALL_TEMPLATES[templateId] || FREE_TEMPLATES['clean-list'];
};

/**
 * Check if a template is available for a plan
 */
export const isTemplateAvailable = (templateId, plan) => {
  const template = ALL_TEMPLATES[templateId];
  if (!template) return false;
  
  if (template.tier === 'FREE') return true;
  if (template.tier === 'PRO' && (plan === 'PRO' || plan === 'ENTERPRISE')) return true;
  
  return false;
};

/**
 * Get the default template for a plan
 */
export const getDefaultTemplate = (plan) => {
  if (plan === 'PRO' || plan === 'ENTERPRISE') {
    return FREE_TEMPLATES['clean-list']; // They can choose any, but start with clean-list
  }
  return FREE_TEMPLATES['clean-list'];
};

/**
 * Apply template to CSS variables (used by PublicMenu)
 * Optionally override primary/secondary colors for PRO users
 */
export const applyTemplate = (template, customizations = {}) => {
  if (!template || !template.colors) return;

  const root = document.documentElement;
  
  // Merge custom colors (only primary and secondary for PRO)
  const colors = {
    ...template.colors,
    ...(customizations.primary && { primary: customizations.primary }),
    ...(customizations.secondary && { secondary: customizations.secondary }),
  };

  // Set color CSS variables
  Object.entries(colors).forEach(([key, value]) => {
    const cssVariableName = `--template-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.setProperty(cssVariableName, value);
  });

  // Set typography CSS variables
  if (template.typography) {
    Object.entries(template.typography).forEach(([key, value]) => {
      const cssVariableName = `--template-typography-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVariableName, String(value));
    });
  }

  // Set spacing CSS variables
  if (template.spacing) {
    Object.entries(template.spacing).forEach(([key, value]) => {
      const cssVariableName = `--template-spacing-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVariableName, value);
    });
  }

  // Set layout mode
  if (template.layout) {
    root.style.setProperty('--template-layout-mode', template.layout.mode);
    root.style.setProperty('--template-item-style', template.layout.itemStyle);
    root.style.setProperty('--template-category-style', template.layout.categoryStyle);
    root.style.setProperty('--template-spacing-mode', template.layout.spacing);
  }

  // Store current template in localStorage for reference
  localStorage.setItem('selectedTemplate', JSON.stringify({
    id: template.id,
    customizations
  }));
};

/**
 * Get template categories for filtering
 */
export const TEMPLATE_CATEGORIES = [
  { id: 'all', name: 'All Templates', nameKey: 'templates.categories.all' },
  { id: 'restaurant', name: 'Restaurants', nameKey: 'templates.categories.restaurants' },
  { id: 'cafe', name: 'Cafe & Bakery', nameKey: 'templates.categories.cafe' },
  { id: 'bar', name: 'Bar & Drinks', nameKey: 'templates.categories.bar' },
  { id: 'fast-food', name: 'Fast Food', nameKey: 'templates.categories.fastFood' },
  { id: 'specialty', name: 'Specialty', nameKey: 'templates.categories.specialty' },
];
